import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { useDispatch, useSelector } from "react-redux";

import store from "../store";
import {
  ECONOMY_MESSAGE_TYPES,
  economyReconnected,
  economySocketMessage,
} from "../store/economy/slice";
import { updateLeaderboardPoints } from "../store/leaderboard/slice";
import {
  addActivity,
  setDeliverableGradeNotification,
} from "../store/notification/index";
import {
  isV6BoardEvent,
  planBoardReconnected,
  planBoardSocketMessage,
} from "../store/planBoard/slice";
import {
  addSessionTask,
  claimSessionTask,
  editSessionTask,
  fetchPlanSession,
  finalizeSessionTask,
  removeSessionTask,
  sameSessionTaskId,
  setSessionPhase,
  setSessionPresence,
  unclaimSessionTask,
} from "../store/planTasks/slice";
import { updateSquadUserPoint } from "../store/tasks/slice";
import { fetchWSAuth, selectActiveSquadId } from "../store/user/slice";

const WebSocketContext = createContext(null);

export const useWebSocket = () => useContext(WebSocketContext);

export const WebSocketProvider = ({ children }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  // M2: the connection belongs to the active delivery context's squad. It is
  // not opened until discovery settles on one, and a context change closes it
  // and opens it again for the new one.
  const activeSquadId = useSelector(selectActiveSquadId);
  const socketRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const planMissedEventRef = useRef(false);
  const [isConnected, setIsConnected] = useState(false);

  const MAX_RECONNECT_ATTEMPTS = 5;
  const BASE_RECONNECT_DELAY = 1000;

  /* M2: every connection belongs to one connection generation. `disconnect`
     advances it, so an auth reply, a reconnect timer or a close event from a
     previous connection (or a previous delivery context) can neither open a
     socket nor discard the current one. `connectRef` is the latest `connect`,
     so a reconnect never runs a stale closure. */
  const connectionGenRef = useRef(0);
  const connectRef = useRef(null);

  const connect = async () => {
    // Don't connect without an active delivery context
    if (!activeSquadId) return;

    // Don't reconnect if already connected
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      return;
    }

    const generation = connectionGenRef.current;
    try {
      const response = await dispatch(fetchWSAuth()).unwrap();
      // Disconnected (or the context moved) while auth was pending.
      if (connectionGenRef.current !== generation) return;
      const socket = new WebSocket(response.sub_endpoint);
      const isCurrent = () => socketRef.current === socket;

      socket.addEventListener("open", () => {
        if (!isCurrent()) return;
        setIsConnected(true);
        reconnectAttempts.current = 0;
        /**
         * W4-F1: WHAT WAS MISSED WHILE THE SOCKET WAS DOWN IS MISSED.
         *
         * There is no backfill on this transport, so a learner whose connection
         * dropped during a level has no way to learn about the pours, the gate
         * or the floor result that landed in the gap. The authenticated read is
         * the only way back to the truth, and this is the one event that says
         * the gap is over. It is dispatched on the FIRST open as well as on a
         * reconnect and costs one read: the slice refuses it until an answer is
         * held, so a cold open is a no-op rather than a duplicate of the
         * dashboard's own first read.
         */
        dispatch(economyReconnected());
        /**
         * v6 plan board (master plan 5.4): the relay is best effort, so a
         * reconnect reconciles the board from the snapshot. A no-op unless a
         * v6 board is in view.
         */
        dispatch(planBoardReconnected());
      });

      socket.addEventListener("message", (event) => {
        if (!isCurrent()) return;
        try {
          /**
           * W4-F1: THE RAW MESSAGE IS NO LONGER LOGGED, AND THAT IS A PRIVACY
           * CHANGE RATHER THAN TIDYING (technical brief section 7).
           *
           * This channel now carries economy, floor, send back and Vault
           * traffic. `console.log(event.data)` put the whole of every one of
           * them into the browser console of every member of the squad, which
           * is a surface the brief's privacy rule covers as squarely as a
           * payload does: no Vault total, no other learner's numbers, no reader
           * field, in any log. The parse failure below is still reported,
           * because a message this client could not read is a fact somebody has
           * to be able to find.
           */
          const data = JSON.parse(event.data);
          const message = data.message;

          if (ECONOMY_MESSAGE_TYPES.includes(message?.type)) {
            /**
             * THE ECONOMY MESSAGES ARE A STALENESS NOTICE AND NOTHING ELSE.
             *
             * Every rejection lives in the slice (duplicate event id, another
             * cohort, a capsule this read does not hold, no read landed yet) so
             * that it is a pure function of the slice's own state and is table
             * tested without a component. Nothing here reads a number out of
             * the payload, and in particular nothing here reaches
             * `updateSquadUserPoint` or `updateLeaderboardPoints`: the shared
             * transport carries `user_id: null` and `user_points: null` by
             * contract, and the self read is the sole authority for squad XP,
             * place and the Vault.
             */
            dispatch(economySocketMessage(message));
          } else if (message?.type === "points") {
            // Always dispatch directly - no buffering
            dispatch(updateSquadUserPoint(message));
            dispatch(updateLeaderboardPoints(message));

            // Deliverable graded notification (activity_type 12)
            if (message.activity_type === 12 && message.user_id === user?.id) {
              let qualityPercentage = null;
              try {
                const info = JSON.parse(message.info);
                qualityPercentage = info.quality_percentage ?? null;
              } catch (e) {
                // info may not be valid JSON or may lack quality_percentage
              }
              dispatch(
                setDeliverableGradeNotification({
                  points: message.points,
                  qualityPercentage,
                  activityKey: `${message.user_id}_${message.activity_type}_${message.id}`,
                  userId: message.user_id,
                }),
              );
            }
          } else if (message?.type === "activity") {
            console.log("[WebSocket] Activity update:", message);
            // Add activity to notification slice
            dispatch(addActivity(message));
          } else if (isV6BoardEvent(message)) {
            /**
             * v6 plan board events carry `board_version`; legacy ones never
             * do. They go to the planBoard slice only, which applies the 5.4
             * rules (identity filter, known+1, buffering, refetch on a gap).
             * The legacy branch below is unchanged and never sees them.
             */
            dispatch(planBoardSocketMessage(message));
          } else if (message?.type?.startsWith("plan_")) {
            const planState = store.getState().planTasks;
            const activeSessionId = planState.session?.id;

            // Only process events for the active session. Number-coerce
            // both sides: the socket relay can stringify sessionId, and a
            // strict !== would then DROP a valid event for the active
            // session (silent, no refetch). Same boundary as taskId.
            if (
              message.sessionId &&
              activeSessionId &&
              Number(message.sessionId) !== Number(activeSessionId)
            ) {
              return; // Wrong session, ignore
            }
            if (!activeSessionId) {
              // Session still loading - mark for refetch after snapshot arrives.
              // The fetchPlanSession.fulfilled reducer will re-fetch if this flag is set.
              planMissedEventRef.current = true;
              return;
            }

            // Helper: refetch full session snapshot
            const refetchSession = () => {
              const session = store.getState().planTasks.session;
              if (session?.squad_id && session?.capsule_id) {
                dispatch(
                  fetchPlanSession({
                    squadId: session.squad_id,
                    capsuleId: session.capsule_id,
                  }),
                );
              }
            };

            // Helper: check if a task exists locally. If not, refetch to reconcile.
            // Type-robust: broadcast taskId can arrive string-typed
            // through the socket relay while local ids are numbers, so a
            // strict === here would wrongly report "not found" and fire a
            // spurious refetch - the root cause of "claim sometimes needs a
            // refresh". See sameSessionTaskId in planTasks/slice.js.
            const taskExists = (taskId) =>
              planState.sessionTasks.some((t) =>
                sameSessionTaskId(t.id, taskId),
              );

            switch (message.type) {
              case "plan_task_created":
                dispatch(addSessionTask(message.task));
                break;
              case "plan_task_deleted":
                dispatch(removeSessionTask(message.taskId));
                break;
              case "plan_task_updated":
                if (!taskExists(message.taskId)) {
                  refetchSession();
                } else {
                  dispatch(
                    editSessionTask({
                      taskId: message.taskId,
                      title: message.title,
                    }),
                  );
                }
                break;
              case "plan_task_claimed":
                if (!taskExists(message.taskId)) {
                  refetchSession();
                } else {
                  dispatch(
                    claimSessionTask({
                      taskId: message.taskId,
                      userId: message.userId,
                    }),
                  );
                }
                break;
              case "plan_task_unclaimed":
                if (!taskExists(message.taskId)) {
                  refetchSession();
                } else {
                  dispatch(unclaimSessionTask({ taskId: message.taskId }));
                }
                break;
              case "plan_phase_changed":
                dispatch(setSessionPhase(message.phase));
                break;
              case "plan_task_finalized":
                if (!taskExists(message.taskId)) {
                  refetchSession();
                } else {
                  dispatch(
                    finalizeSessionTask({
                      taskId: message.taskId,
                      questId: message.questId,
                    }),
                  );
                }
                break;
              case "plan_presence_changed":
                dispatch(setSessionPresence(message.presence));
                break;
              default:
                refetchSession();
                break;
            }
          } else if (["cursor", "typing", "presence"].includes(message?.type)) {
            presenceSubscribers.current.forEach((cb) => cb(message));
          }
        } catch (error) {
          console.error("[WebSocket] Error parsing message:", error);
        }
      });

      socket.addEventListener("close", (event) => {
        // An old socket closing (an intentional disconnect, or a connection
        // already replaced) must not touch the current one or reconnect.
        if (!isCurrent()) return;
        console.log("[WebSocket] Disconnected", event.code);
        setIsConnected(false);
        socketRef.current = null;

        // Attempt reconnection with exponential backoff
        if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
          const delay =
            BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttempts.current);
          console.log(
            `[WebSocket] Reconnecting in ${delay}ms (attempt ${reconnectAttempts.current + 1})`,
          );
          reconnectTimeoutRef.current = setTimeout(() => {
            if (connectionGenRef.current !== generation) return;
            reconnectAttempts.current++;
            connectRef.current?.();
          }, delay);
        }
      });

      socket.addEventListener("error", (error) => {
        console.error("[WebSocket] Error:", error);
      });

      socketRef.current = socket;
    } catch (error) {
      console.error("[WebSocket] Failed to get auth:", error);
    }
  };

  connectRef.current = connect;

  const disconnect = () => {
    connectionGenRef.current += 1;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    const socket = socketRef.current;
    // Detached before closing, so its own close event is ignored.
    socketRef.current = null;
    if (socket) socket.close();
    setIsConnected(false);
  };

  const sendMessage = useCallback((data) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(data));
    }
  }, []);

  const presenceSubscribers = useRef(new Set());

  const subscribePresence = useCallback((callback) => {
    presenceSubscribers.current.add(callback);
    return () => presenceSubscribers.current.delete(callback);
  }, []);

  // If plan events were dropped during session loading, refetch once session is available
  const planSession = useSelector((state) => state.planTasks?.session);
  useEffect(() => {
    if (planSession?.id && planMissedEventRef.current) {
      planMissedEventRef.current = false;
      dispatch(
        fetchPlanSession({
          squadId: planSession.squad_id,
          capsuleId: planSession.capsule_id,
        }),
      );
    }
  }, [planSession?.id, dispatch]);

  // Connect when there is an active delivery context
  useEffect(() => {
    if (activeSquadId) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [activeSquadId]);

  const value = {
    isConnected,
    reconnect: connect,
    sendMessage,
    subscribePresence,
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
